/**
 * Phase 2 Uploads — image / video 上傳資產型別
 * -----------------------------------------------------------------------------
 * 對齊後端 ``/hai-creative/api/uploads/{images,videos}/*`` endpoints。後端送 JSON
 * 是 snake_case；前端業務層一律 camelCase。HTTP adapter（``httpUploadsService``）
 * 負責 dto → camelCase mapping，UI 永遠看不到 snake_case 欄位。
 *
 * 「為什麼 image / video 各用獨立 interface 而不是 union + discriminator」：
 *   - Phase 2 doc 指明：避免誤用 type guard `image is UploadedImage` 在 video 上意外通過
 *   - 兩 type 共用欄位封進 :type:`UploadedAssetBase`，差異點（``durationSeconds``）放
 *     ``UploadedVideo`` 自己擴
 *   - assetLibrary facade 對 UI 也採同樣分流：``uploadImage(file)`` vs ``uploadVideo(file)``
 *
 * @see docs/UPLOADS.md
 */

/** Phase 2 doc 指定的上傳資產分類；之後加 audio / 3d 模型會在這擴。 */
export type UploadAssetType = 'image' | 'video';

// ─── DTO（snake_case，後端原樣 JSON）─────────────────────────────────────────

/** ``POST /uploads/{type}/intent`` ``result`` schema。 */
export interface UploadIntentResultDto {
  upload_id: string;
  upload_url: string;
  expires_datetime: string;
  max_size_bytes: number;
  allowed_mime_types: string[];
  /**
   * video intent 專屬(docs/107 A-1;BE E-5):後端鎖死的時長上限(秒)。
   * image intent 恆缺席 / null;舊 BE(欄位未上)也缺席 → optional,讀取端 fallback。
   */
  max_duration_seconds?: number | null;
}

/** image / video 共用欄位（snake_case）。 */
export interface UploadedAssetBaseDto {
  upload_id: string;
  mime_type: string;
  size_bytes: number;
  // R50.3 — Beta v0.0.1 backend 砍 `gcs_path` field(BE commit 67f0826 P0 security:
  // bucket name reconnaissance leak)。FE 拿 fresh `download_url` 即可,瀏覽器無法
  // 直接讀 GCS object,只能透過 backend signed URL。
  download_url: string;
  download_url_expires_datetime: string;
  width: number | null;
  height: number | null;
  created_datetime: string;
  updated_datetime: string;
  deleted_datetime: string | null;
}

/** ``POST /uploads/images/finalize`` 等 image endpoints 的 ``result``。 */
export type UploadedImageDto = UploadedAssetBaseDto;

/** ``POST /uploads/videos/finalize`` 等 video endpoints 的 ``result``。 */
export interface UploadedVideoDto extends UploadedAssetBaseDto {
  duration_seconds: number;
}


/**
 * @deprecated 通用 list 信封已升格中性名 :type:`ListResultDto`(``types/api.ts``)。
 * 此別名僅為向後相容保留;新程式碼直接用 `ListResultDto`。
 */
export type { ListResultDto as UploadedListDto } from './api';

// ─── 前端 camelCase 型別 ─────────────────────────────────────────────────────

/** :type:`UploadIntentResultDto` 的前端對應。 */
export interface UploadIntent {
  uploadId: string;
  uploadUrl: string;
  /** ISO 8601 with timezone offset（UTC = ``+00:00``，非 ``Z`` 後綴）。 */
  expiresDatetime: string;
  maxSizeBytes: number;
  allowedMimeTypes: string[];
  /** video 專屬時長上限(秒);image / 舊 BE 缺欄 → null(docs/107 A-1)。 */
  maxDurationSeconds: number | null;
}

/** image / video 共用欄位的 base（純前端便利；非 runtime 繼承關係）。 */
export interface UploadedAssetBase {
  uploadId: string;
  mimeType: string;
  sizeBytes: number;
  /**
   * R50.3 — Beta v0.0.1 backend 砍 `gcsPath`(P0 security:bucket name reconnaissance
   * leak)。FE 持久化只存 `uploadId`,顯示時呼 `GET /uploads/images/{uploadId}` 拿
   * fresh `downloadUrl`(每次 endpoint response 即時簽,不存 Firestore)。
   *
   * Signed read URL,TTL = 15 分鐘(後端決定)。過期重打 GET 取新。
   */
  downloadUrl: string;
  downloadUrlExpiresDatetime: string;
  width: number | null;
  height: number | null;
  createdDatetime: string;
  updatedDatetime: string;
  /** 軟刪除時間；未刪除為 ``null``。delete 端點回完整 entity，此值會有實值。 */
  deletedDatetime: string | null;
}

export type UploadedImage = UploadedAssetBase;

export interface UploadedVideo extends UploadedAssetBase {
  durationSeconds: number;
}


// ─── DTO → camelCase mapping ─────────────────────────────────────────────────

export function mapUploadIntentDto(dto: UploadIntentResultDto): UploadIntent {
  return {
    uploadId: dto.upload_id,
    uploadUrl: dto.upload_url,
    expiresDatetime: dto.expires_datetime,
    maxDurationSeconds: typeof dto.max_duration_seconds === 'number' ? dto.max_duration_seconds : null,
    maxSizeBytes: dto.max_size_bytes,
    allowedMimeTypes: dto.allowed_mime_types,
  };
}

function mapUploadedAssetBaseDto(dto: UploadedAssetBaseDto): UploadedAssetBase {
  return {
    uploadId: dto.upload_id,
    mimeType: dto.mime_type,
    sizeBytes: dto.size_bytes,
    // R50.3 — gcs_path 已從 BE response 砍除(P0 security)
    downloadUrl: dto.download_url,
    downloadUrlExpiresDatetime: dto.download_url_expires_datetime,
    width: dto.width,
    height: dto.height,
    createdDatetime: dto.created_datetime,
    updatedDatetime: dto.updated_datetime,
    deletedDatetime: dto.deleted_datetime,
  };
}

export function mapUploadedImageDto(dto: UploadedImageDto): UploadedImage {
  return mapUploadedAssetBaseDto(dto);
}

export function mapUploadedVideoDto(dto: UploadedVideoDto): UploadedVideo {
  return {
    ...mapUploadedAssetBaseDto(dto),
    durationSeconds: dto.duration_seconds,
  };
}

